// Past tickets the RAG match named; opening one shows its thread over the conversation.

import { useState } from 'react';
import { Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import type { SimilarTicketMatch } from './types';

/** Rows shown before the list scrolls in place, and the row height that bounds it. */
const VISIBLE_ROWS = 7;
const ROW_HEIGHT_PX = 34;
/** Keeps the active row's fill off the hovered row's, so the two never merge into one block. */
const ROW_GAP_PX = 2;

/** Match strength as a percentage; the legacy column carries none. */
export function ScoreChip({ similarity }: { similarity: number | null }) {
  if (similarity === null) return null;
  return (
    <span className="shrink-0 rounded-2xl bg-green-100 px-2.5 py-1 text-[12px] leading-4 text-green-500">
      {Math.round(similarity * 100)} %
    </span>
  );
}

export function SimilarTicketsCard({
  matches,
  activeConversationId,
  onOpenTicket,
}: {
  matches: SimilarTicketMatch[];
  /** The ticket whose thread is open, so the row it came from stays marked. */
  activeConversationId: string | null;
  onOpenTicket: (match: SimilarTicketMatch) => void;
}) {
  const [open, setOpen] = useState(true);
  const scrolls = matches.length > VISIBLE_ROWS;

  return (
    <div className="flex w-full flex-col border-l-4 border-solid border-transparent bg-white">
      <div className={cn('transition-[padding] duration-300 ease-in-out motion-reduce:transition-none flex w-full flex-col px-5 pt-4', open ? 'pb-2' : 'pb-4')}>
        <CollapseHeader open={open} label="Similar Tickets" variant="chevron" onToggle={() => setOpen((prev) => !prev)} className="h-5">
          <div className="flex min-w-0 items-center gap-2">
            <Ticket className="size-5 shrink-0 text-text-body" />
            <p className="truncate text-[14px] font-semibold leading-[1.1] text-text-body">Similar Tickets</p>
          </div>
        </CollapseHeader>
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col px-3 pb-3">
          {/* Bounded to seven whole rows, so the eighth peeks and the cards below never move. */}
          <div
            style={{
              gap: ROW_GAP_PX,
              ...(scrolls ? { maxHeight: VISIBLE_ROWS * ROW_HEIGHT_PX + (VISIBLE_ROWS - 1) * ROW_GAP_PX } : {}),
            }}
            className={cn('flex w-full flex-col', scrolls && 'overflow-y-auto [scrollbar-gutter:stable]')}
          >
            {matches.map((match) => (
              <button
                key={match.conversationId}
                type="button"
                onClick={() => onOpenTicket(match)}
                style={{ height: ROW_HEIGHT_PX }}
                aria-current={activeConversationId === match.conversationId}
                className={cn(
                  'flex w-full shrink-0 cursor-pointer items-center justify-between gap-2 rounded-lg px-2 text-left transition-colors hover:bg-gray-100',
                  activeConversationId === match.conversationId && 'bg-gray-100',
                )}
              >
                <span className="truncate text-[13px] leading-[18px] text-text-body">#{match.conversationId}</span>
                <ScoreChip similarity={match.similarity} />
              </button>
            ))}
          </div>
        </div>
      </CollapseBody>
    </div>
  );
}
